import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {AlertCircle} from "lucide-react";
import CourseCard from "./CourseCard";
import {useUserPublicCatalog} from "../../store/user";
import {mapPublicCourse} from "./publicCourseMap";

const PAGE_SIZE = 8;

function Chip({active, onClick, children}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-all ${
        active
          ? "border-blue-900 bg-blue-900 text-white"
          : "border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-800"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Landing "Course marketplace" — backed by the public catalog endpoints:
 *   GET /api/public/catalog/courses?page&limit&sort&categoryId
 *   GET /api/public/catalog/categories
 */
export default function CourseDiscovery({pageSize = PAGE_SIZE}) {
  const navigate = useNavigate();
  const {
    publicCourses,
    publicCoursesLoading,
    publicCoursesError,
    fetchPublicCourses,
    publicCategories,
    fetchPublicCategories,
    publicTopics,
    fetchPublicTopics,
  } = useUserPublicCatalog();

  const [categoryId, setCategoryId] = useState("All");
  const [topic, setTopic] = useState("");
  const [hasLoaded, setHasLoaded] = useState(false);

  // GET /api/public/catalog/categories
  useEffect(() => {
    fetchPublicCategories();
    fetchPublicTopics();
  }, [fetchPublicCategories, fetchPublicTopics]);

  // GET /api/public/catalog/courses
  useEffect(() => {
    let active = true;
    const params = {page: 1, limit: pageSize, sort: "newest"};
    if (categoryId !== "All") params.categoryId = categoryId;
    if (topic) params.topic = topic;
    fetchPublicCourses(params).finally(() => {
      if (active) setHasLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [categoryId, topic, pageSize, fetchPublicCourses]);

  const courses = useMemo(
    () => (publicCourses || []).map(mapPublicCourse),
    [publicCourses]
  );

  const categories = useMemo(
    () =>
      (publicCategories || []).map((cat) => ({
        id: cat.id ?? cat._id,
        name: cat.name ?? cat.title ?? "Untitled",
      })),
    [publicCategories]
  );
  const topics = useMemo(
    () => (publicTopics || []).map((item) => typeof item === "string" ? item : item.name).filter(Boolean),
    [publicTopics]
  );

  return (
    <section id="discover" className="bg-white px-4 py-8">
      <div className="mx-auto max-w-7xl">
        {/* Heading */}
        <header className="mx-auto mb-8 max-w-2xl text-center" data-aos="fade-up">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-blue-700">
            Course marketplace
          </span>
          <h2 className="mt-4 text-3xl tracking-tight text-slate-900 sm:text-4xl">
            Discover Your Next Skill
          </h2>
        </header>

        {/* Category chips */}
        <div
          className="mb-6 flex gap-2 overflow-x-auto border-b border-slate-200 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          role="tablist"
          aria-label="Course categories"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          <Chip active={categoryId === "All"} onClick={() => setCategoryId("All")}>
            All
          </Chip>
          {categories.map((cat) => (
            <Chip
              key={cat.id}
              active={categoryId === cat.id}
              onClick={() => setCategoryId(cat.id)}
            >
              {cat.name}
            </Chip>
          ))}
        </div>

        {topics.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2" aria-label="Course topics">
            <Chip active={!topic} onClick={() => setTopic("")}>All topics</Chip>
            {topics.map((item) => (
              <Chip key={item} active={topic === item} onClick={() => setTopic(item)}>{item}</Chip>
            ))}
          </div>
        )}

        {/* Results */}
        {publicCoursesLoading || !hasLoaded ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({length: Math.min(pageSize, 4)}).map((_, i) => (
              <div
                key={i}
                className="h-64 animate-pulse rounded-sm border border-slate-200 bg-slate-100"
              />
            ))}
          </div>
        ) : publicCoursesError ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <AlertCircle className="h-8 w-8 text-red-500" />
            <p className="text-sm text-slate-600">{publicCoursesError}</p>
            <button
              type="button"
              onClick={() => {
                const params = {page: 1, limit: pageSize, sort: "newest"};
                if (categoryId !== "All") params.categoryId = categoryId;
                if (topic) params.topic = topic;
                fetchPublicCourses(params);
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-blue-300 hover:text-blue-800"
            >
              Retry
            </button>
          </div>
        ) : courses.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500">
            No courses found{categoryId !== "All" ? " in this category" : ""} yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {courses.map((course) => (
              <CourseCard dense key={course.id} course={course} />
            ))}
          </div>
        )}

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => navigate("/courses")}
            className="text-sm font-semibold text-blue-800 underline underline-offset-4 transition-colors hover:text-blue-950"
          >
            View more courses
          </button>
        </div>
      </div>
    </section>
  );
}
